// Failure and Action code description lookups
// Used to display human-readable labels instead of cryptic codes

export interface CodeDescription {
  code: string;
  label: string;
  category?: string;
}

export const failureCodeDescriptions: Record<string, CodeDescription> = {
  'EJECT-01': { code: 'EJECT-01', label: 'Ejector pin stuck or broken', category: 'Ejector System' },
  'EJECT-02': { code: 'EJECT-02', label: 'Ejector plate misaligned', category: 'Ejector System' },
  'COOL-01': { code: 'COOL-01', label: 'Cooling circuit blockage', category: 'Cooling' },
  'COOL-02': { code: 'COOL-02', label: 'Cooling leak detected', category: 'Cooling' },
  'GATE-01': { code: 'GATE-01', label: 'Gate blocked or damaged', category: 'Runner/Gate' },
  'GATE-02': { code: 'GATE-02', label: 'Gate vestige out of spec', category: 'Runner/Gate' },
  'ALIGN-01': { code: 'ALIGN-01', label: 'Parting line flash', category: 'Alignment/Wear' },
  'ALIGN-02': { code: 'ALIGN-02', label: 'Core shift detected', category: 'Alignment/Wear' },
  'VENT-01': { code: 'VENT-01', label: 'Venting insufficient / burn marks', category: 'Venting' },
  'HR-01': { code: 'HR-01', label: 'Hot runner heater failure', category: 'Hot Runner' },
  'HR-02': { code: 'HR-02', label: 'Hot runner thermocouple failure', category: 'Hot Runner' },
  'HR-03': { code: 'HR-03', label: 'Hot runner nozzle tip damaged', category: 'Hot Runner' },
  'SURF-01': { code: 'SURF-01', label: 'Surface damage or scratch', category: 'Surface/Polish' },
  'SURF-02': { code: 'SURF-02', label: 'Cavity corrosion', category: 'Surface/Polish' },
  'CONT-01': { code: 'CONT-01', label: 'Material contamination / residue', category: 'Contamination' },
  'WEAR-01': { code: 'WEAR-01', label: 'Excessive wear on moving parts', category: 'Wear' },
  'SEAL-01': { code: 'SEAL-01', label: 'O-ring or seal failure', category: 'Sealing' },
  'SHORT-01': { code: 'SHORT-01', label: 'Short shot / incomplete fill', category: 'Process' },
  'PM-LIGHT': { code: 'PM-LIGHT', label: 'Light PM (scheduled)', category: 'Preventive' },
  'PM-FULL': { code: 'PM-FULL', label: 'Full PM (scheduled)', category: 'Preventive' },
  'PM-HR': { code: 'PM-HR', label: 'Hot runner PM (scheduled)', category: 'Preventive' },
  'PM-COOL': { code: 'PM-COOL', label: 'Cooling system PM (scheduled)', category: 'Preventive' },
  'UNK': { code: 'UNK', label: 'Unknown / Other', category: 'Unknown' },
};

export const actionCodeDescriptions: Record<string, CodeDescription> = {
  'CLEAN': { code: 'CLEAN', label: 'Cleaned and flushed' },
  'REPL': { code: 'REPL', label: 'Replaced component' },
  'ADJ': { code: 'ADJ', label: 'Adjusted / re-aligned' },
  'POLISH': { code: 'POLISH', label: 'Polished surface' },
  'REMACH': { code: 'REMACH', label: 'Re-machined' },
  'MOD': { code: 'MOD', label: 'Modified / improved' },
  'LUBE': { code: 'LUBE', label: 'Lubricated' },
  'UNBLK': { code: 'UNBLK', label: 'Unblocked passage' },
  'REWIRE': { code: 'REWIRE', label: 'Rewired electrical' },
  'TEST': { code: 'TEST', label: 'Tested and validated' },
  'PM': { code: 'PM', label: 'PM completed per checklist' },
  'SEAL': { code: 'SEAL', label: 'Replaced seals / O-rings' },
  'WELD': { code: 'WELD', label: 'Welded and repaired' },
  'OTHER': { code: 'OTHER', label: 'Other action' },
};

/** Get a human-readable failure description. Falls back to code with "Unknown code" note. */
export function getFailureLabel(code: string | undefined): string | undefined {
  if (!code) return undefined;
  const desc = failureCodeDescriptions[code];
  return desc ? `${desc.label} (${code})` : `${code} — Unknown code`;
}

/** Get a human-readable action description. Falls back to code with "Unknown code" note. */
export function getActionLabel(code: string | undefined): string | undefined {
  if (!code) return undefined;
  const desc = actionCodeDescriptions[code];
  return desc ? `${desc.label} (${code})` : `${code} — Unknown code`;
}
